/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { useInView } from 'react-intersection-observer';
import { dayjs } from 'helpers/dayjs';
import isEmpty from 'lodash/isEmpty';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { DragIcon, QrTinyIcon } from 'assets/icons';
import {
  isAnyLoggedUser,
  isReadOnly,
  isRestrictedVault,
  hasVaultAccess,
  showTextEllipsis,
  classnames,
  isTemporaryUser,
  isOwnerOrClipOwner,
  copyToClipboard,
} from 'helpers/utils';
import thumbnailDefault from 'assets/images/thumbnail-default.png';
import { Breakpoints, ClipActions } from 'common/enums';
import { useMediaQuery } from 'hooks/use-media-query';
import { Menu, PositionType } from 'common/menu';
import { createUrlToDownloadFile } from 'helpers/file-utils';
import { trackCustomEvent } from 'helpers/analytics';
import { getRouteFor } from 'routes/route-helpers';
import { apiBaseURL } from 'config/constants';
import { RouteName } from 'routes';
import { useClickOutside } from 'hooks/click-outside';
import { ModalWarning } from 'common/modal-warning';
import { ButtonStyle } from 'common/button';
import { ImageAudio } from 'common/image-audio';
import styles from './clip-item.module.scss';

interface ClipItemProps {
  clip: ClipType;
  clipsRecorded: ClipType[];
  selectedClip: ClipType;
  index: number;
  vault: VaultType;
  user: UserType;
  publicView: boolean;
  notifyStopClip?: () => void;
  notifyClipAction: (
    action: ClipActions,
    clip: ClipType,
    extraData?: ClipExtraDataType,
  ) => void;
  setShowModalSignUp: (show: boolean) => void;
}

type DeleteClipModalProps = {
  show: boolean;
  clip?: ClipType;
};

export const ClipItem: React.FC<ClipItemProps> = ({
  clip,
  clipsRecorded,
  selectedClip,
  index,
  vault,
  user,
  publicView,
  notifyStopClip,
  notifyClipAction,
  setShowModalSignUp,
}) => {
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const [clipHoveredId, setClipHoveredId] = useState<number | null>(null);
  const [idClipOptionsSelected, setIdClipOptionsSelected] = useState(-1);
  const [showClipOptionsMenu, setShowClipOptionsMenu] = useState(false);
  const [deleteClipModal, setDeleteClipModal] = useState<DeleteClipModalProps>({
    show: false,
  });
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '300px',
  });
  const containerClipOptionsRef = useRef(null);

  useClickOutside(containerClipOptionsRef, () => {
    setIdClipOptionsSelected(-1);
    setShowClipOptionsMenu(false);
  });

  const showCopyLinkButton = () => (
    <button
      type="button"
      onClick={
        isTemporaryUser(user)
          ? () => setShowModalSignUp(true)
          : () => {
              copyToClipboard(
                `${apiBaseURL}${getRouteFor(RouteName.ClipDetailGuest, {
                  vaultId: vault.id,
                  clipId: clip.id,
                })}`,
              );
              trackCustomEvent('copyClipLink', { step: 'clipMenu' });
            }
      }
      className="text__body__regular__medium__gray700"
    >
      Copy link
    </button>
  );

  const showDeleteClipButton = () => (
    <button
      type="button"
      onClick={() => {
        notifyStopClip!();
        setDeleteClipModal({ show: true, clip });
      }}
      className="text__body__regular__medium__gray700"
    >
      Delete clip
    </button>
  );

  const showDeleteClipModalComponent = () => (
    <ModalWarning
      title="Deleting clip"
      content="Are you sure you want to delete this clip? Deleted clips cannot be recovered."
      successButtonText="Delete clip"
      successStyleButton={ButtonStyle.RedFilled}
      closeFn={() => setDeleteClipModal({ show: false })}
      successFn={() => {
        notifyClipAction(ClipActions.delete, deleteClipModal.clip!);
        setShowClipOptionsMenu(false);
        setDeleteClipModal({ show: false });
      }}
    />
  );

  const showEditClipButton = () => (
    <button
      type="button"
      onClick={() => {
        notifyClipAction(ClipActions.editClip, clip);
      }}
      className="text__body__regular__medium__gray700"
    >
      Edit info
    </button>
  );

  const showClipStatusOption = () => (
    <button
      type="button"
      onClick={() => notifyClipAction(ClipActions.togglePrivate, clip)}
      className="text__body__regular__medium__gray700"
    >
      {clip.isPrivate ? 'Make public' : 'Make private'}
    </button>
  );

  const showClipAudioOnlyOption = () => (
    <button
      type="button"
      onClick={() => notifyClipAction(ClipActions.toggleAudioOnly, clip)}
      className="text__body__regular__medium__gray700"
    >
      {clip.isAudioOnly ? 'Turn into video' : 'Turn into audio'}
    </button>
  );

  const showClipsOptionsMenu = () => {
    const indexClip = clipsRecorded?.findIndex(
      (clipItem) => clipItem.id === clip.id,
    );

    const showTopLeft =
      indexClip &&
      vault.clips &&
      vault.clips.length > 4 &&
      indexClip > vault.clips!.length - 4;
    return (
      <div ref={containerClipOptionsRef}>
        <Menu
          position={
            showTopLeft ? PositionType.TopLeft : PositionType.BottomLeft
          }
          width={styles.clipOptionsMenu}
          alwaysVisible
        >
          {hasVaultAccess(user, vault) && showClipStatusOption()}
          {hasVaultAccess(user, vault) && showClipAudioOnlyOption()}

          {!clip.isPrivate && showCopyLinkButton()}

          {hasVaultAccess(user, vault) && <hr />}

          {hasVaultAccess(user, vault) && showEditClipButton()}
          {isOwnerOrClipOwner(user, vault, clip) && showDeleteClipButton()}

          <button
            type="button"
            className={classnames(
              styles.downloadButton,
              'text__body__regular__medium__gray700',
            )}
            onClick={
              isTemporaryUser(user)
                ? () => setShowModalSignUp(true)
                : () => trackCustomEvent('downloadClip', { step: 'clipMenu' })
            }
          >
            {isTemporaryUser(user) ? (
              <div className="text__body__regular__medium__gray700">
                Download clip
              </div>
            ) : (
              <a
                className="text__body__regular__medium__gray700"
                href={createUrlToDownloadFile(clip.videoUrl!, clip.name)}
                download={clip.name}
              >
                Download clip
              </a>
            )}
          </button>
          {clip.customThumbnail && (
            <button
              type="button"
              className={classnames(
                styles.downloadButton,
                'text__body__regular__medium__gray700',
              )}
              onClick={
                isTemporaryUser(user)
                  ? () => setShowModalSignUp(true)
                  : undefined
              }
            >
              {isTemporaryUser(user) ? (
                <div className="text__body__regular__medium__gray700">
                  Download picture
                </div>
              ) : (
                <a
                  className="text__body__regular__medium__gray700"
                  href={createUrlToDownloadFile(
                    clip.customThumbnail,
                    clip.name,
                  )}
                  download={clip.name}
                >
                  Download picture
                </a>
              )}
            </button>
          )}
        </Menu>
      </div>
    );
  };

  const showThreeDotsOptions = () => (
    <div className={styles.clipsDots}>
      <button
        type="button"
        key={clip.id}
        className={classnames(globalStyles.threeDots, styles.dotsColor)}
        onClick={() => {
          setShowClipOptionsMenu(!showClipOptionsMenu);
          setIdClipOptionsSelected(clip.id);
        }}
      >
        {idClipOptionsSelected === clip.id &&
          showClipOptionsMenu &&
          showClipsOptionsMenu()}
      </button>
    </div>
  );

  return (
    <>
      {deleteClipModal.show && showDeleteClipModalComponent()}

      <Draggable
        key={index}
        draggableId={`${index}`}
        index={index}
        isDragDisabled={
          isReadOnly(vault) ||
          !isAnyLoggedUser(user) ||
          !hasVaultAccess(user, vault)
        }
      >
        {(provided, snapshot) => (
          <div
            role="listbox"
            key={clip.id}
            className={classnames(
              styles.clipItem,
              clip.id === selectedClip.id ? styles.clipSelected : '',
            )}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onMouseEnter={() => setClipHoveredId(clip.id)}
            onMouseLeave={() => setClipHoveredId(null)}
            tabIndex={0}
          >
            <div
              onClick={() => {
                notifyClipAction(ClipActions.select, clip, {
                  autoPlaying: true,
                });
              }}
              tabIndex={0}
              className={styles.numberAndInfo}
              role="button"
            >
              <div
                className={classnames(styles.numberAndInfo, {
                  [styles.clipSelected]: clip.id === selectedClip.id,
                })}
              >
                <span
                  className={classnames(
                    styles.numberInList,
                    'text__body__regular__tiny__gray700',
                    {
                      [styles.draggingIcon]: snapshot.isDragging,
                      [styles.clipSelected]: clip.id === selectedClip.id,
                    },
                  )}
                >
                  {snapshot.isDragging ||
                  (hasVaultAccess(user, vault) && clip.id === clipHoveredId) ? (
                    <DragIcon />
                  ) : (
                    index + 1
                  )}
                </span>

                <div className={styles.clipContent}>
                  <div className={styles.picture} ref={ref}>
                    {inView &&
                      (clip.isAudioOnly ? (
                        <ImageAudio src={clip.customThumbnail} alt="" />
                      ) : (
                        <img
                          src={
                            clip.customThumbnail ||
                            clip.thumbnailUrl ||
                            thumbnailDefault
                          }
                          alt=""
                          loading="lazy"
                        />
                      ))}
                  </div>

                  <div className={styles.descriptionAndStatus}>
                    <div
                      className={
                        clip.id === selectedClip.id
                          ? 'text__body__regular__small__gray800'
                          : 'text__body__regular__small__gray700'
                      }
                    >
                      {isMobile ? showTextEllipsis(clip.name, 35) : clip.name}
                    </div>

                    <div className={styles.clipLengthAndStatus}>
                      <div className={styles.lengthAndDate}>
                        <span className="text__body__regular__overline__gray700">
                          {dayjs
                            .duration(clip.length, 'seconds')
                            .format('mm:ss')}
                          {'  '}-{'  '}
                          {dayjs(clip.recordedAt)
                            .format('MMMM D, YYYY')
                            .toUpperCase()}
                        </span>
                      </div>

                      <div className={styles.infoClip}>
                        {!isEmpty(clip.qrCodes) && (
                          <QrTinyIcon className={styles.qrIcon} />
                        )}
                        {clip.isPrivate && (
                          <div
                            className={classnames(
                              'text__body__semi__bold__overline__gray700',
                              styles.privateText,
                            )}
                          >
                            PRIVATE
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!publicView &&
              !isRestrictedVault(vault) &&
              !isReadOnly(vault) &&
              showThreeDotsOptions()}
          </div>
        )}
      </Draggable>
    </>
  );
};
